import { AxiosError } from 'axios';
import i18n from '@/i18n';
import { del, get, post, put } from '@/utils';
import {
	IServerColumnConfig,
	IServerTableConfig
} from '@/components/table/column-config';
import { IProjectType, IProjectTypeCreator, IProjectTypePayload } from './types';

type ProjectTypesResponse = {
	items?: IProjectType[];
	pageNumber?: number;
	totalPages?: number;
	totalCount?: number;
	orderBy?: string | null;
	ascending?: boolean;
	hasPreviousPage?: boolean;
	hasNextPage?: boolean;
};

const buildProjectTypesQuery = (
	page: number,
	items: number,
	query?: string,
	administrationId?: string | number
): string => {
	const params = new URLSearchParams((query || '').replace(/^\?/, ''));
	params.set('PageNumber', String(page + 1));
	params.set('PageSize', String(items));
	if (administrationId && administrationId !== 'all' && !params.has('AdministrationId')) {
		params.set('AdministrationId', String(administrationId));
	}
	const queryString = params.toString();
	return queryString ? `?${queryString}` : '';
};

async function fetchTypes(
	page: number,
	items: number,
	onResponse?: (page: number, items: number) => void,
	_getAll?: boolean,
	fspId?: number,
	_owned?: boolean
): Promise<IProjectType[]> {
	try {
		const administrationId = fspId || undefined;
		const res = await get(
			`api/project-types${buildProjectTypesQuery(page, items, undefined, administrationId)}`
		);
		const data = res.data as ProjectTypesResponse;
		onResponse?.(data.totalPages || 1, data.totalCount || 0);
		return (data.items || []);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('projectTypes.errorLoad'));
	}
}

async function fetchTypesQuery(
	query: string,
	page: number,
	items: number,
	onResponse?: (page: number, items: number) => void,
	_getAll?: boolean,
	fspId?: number,
	_owned?: boolean
): Promise<IProjectType[]> {
	try {
		const administrationId = fspId || undefined;
		const res = await get(
			`api/project-types${buildProjectTypesQuery(page, items, query, administrationId)}`
		);
		const data = res.data as ProjectTypesResponse;
		onResponse?.(data.totalPages || 1, data.totalCount || 0);
		return (data.items || []);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('projectTypes.errorLoad'));
	}
}

// GET api/project-types/{id} → un singur tip de proiect (aceeași formă ca item-ul din listă).
async function fetchSingleType(typeId: number): Promise<IProjectType> {
	try {
		const res = await get(`api/project-types/${typeId}`);
		return res.data as IProjectType;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('projectTypes.errorLoadOne'));
	}
}

async function deleteType(typeId: number): Promise<string | number> {
	try {
		const res = await del(`api/project-types/${typeId}`);
		// BE poate răspunde cu orice 2xx (200/204) la ștergere.
		return res?.status && res.status >= 200 && res.status < 300 ? res.status : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('projectTypes.errorDelete'));
	}
}

async function postTypes(
	typeObj: IProjectTypePayload,
	onSuccessResponse?: (res: IProjectType) => void
): Promise<string | number> {
	try {
		const res = await post('api/project-types', { ...typeObj });
		const nextProjectType = res?.data?.projectType ?? res?.data;
		onSuccessResponse && res?.status === 201 && onSuccessResponse(nextProjectType);
		return res?.status === 201 ? 201 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('projectTypes.errorCreate'));
	}
}

async function patchTypes(
	typeId: number,
	updateObj: IProjectTypePayload
): Promise<string | number> {
	try {
		const res = await put(`api/project-types/${typeId}`, { ...updateObj });
		return res?.status === 200 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('projectTypes.errorUpdate'));
	}
}

// GET api/project-types/creators → utilizatorii care au creat tipuri (pentru filtrul „Creat de").
async function fetchProjectTypeCreators(): Promise<IProjectTypeCreator[]> {
	try {
		const res = await get('api/project-types/creators');
		const data = Array.isArray(res.data) ? (res.data as IProjectTypeCreator[]) : [];
		return data
			.filter((creator) => creator && creator.id !== undefined && creator.id !== null)
			.map((creator) => ({
				id: Number(creator.id),
				firstName: creator.firstName ?? '',
				lastName: creator.lastName ?? ''
			}));
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('projectTypes.errorCreators'));
	}
}

// GET api/project-types/table-config — configurația coloanelor tabelului Tipuri de
// proiect (chei, etichete, ordine, vizibilitate/sortabilitate implicite). Se corelează
// în pagină cu definițiile locale prin applyServerColumnConfig.
async function fetchTypesTableConfig(): Promise<IServerColumnConfig[]> {
	try {
		const res = await get('api/project-types/table-config');
		const data = res.data as IServerTableConfig;
		return data?.columns ?? [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reușit să încărcăm configurația tabelului.');
	}
}

export {
	fetchTypes,
	fetchTypesTableConfig,
	fetchSingleType,
	deleteType,
	patchTypes,
	postTypes,
	fetchTypesQuery,
	fetchProjectTypeCreators
};
